import {
    Person,
    HubPersona,
    PrivateDistributionList,
    getHubPersonaDisplayNameFirstLast,
    getHubPersonaDisplayNameLastFirst,
    FavoriteEntity,
    PersonType,
    toEscapedRegex,
} from '../../../shared';
import type { QueryData } from '../../models/query';
import type { HubPersonasMap } from '../../../appstate';

import levenshtein from 'fast-levenshtein';
const MAX_LEVENSHTEIN_DISTANCE = 2;
const MIN_LEVENSHTEIN_WORD_LENGTH = 5;

// Search results are sorted by highest relevancy first. Match in display name should be
// ranked highest. These values are set based on best guess.
const RELEVANCY_SCORE_DISPLAY_NAME = -5;
const RELEVANCY_SCORE_EMAIL = -10;
const RELEVANCY_SCORE_PHONE = -15;
const RELEVANCY_SCORE_NOTES = -20;
const RELEVANCY_SCORE_COMPANY = -25;
const RELEVANCY_SCORE_TITLE = -30;
const RELEVANCY_SCORE_LOCATION = -35;

export function searchPeopleLocallyWithFolderMap(
    queryString: string,
    hubPersonasMap: HubPersonasMap,
    folderHubPersonaIds: string[]
): HubPersona[] {
    if (!folderHubPersonaIds) {
        return [] as HubPersona[];
    }

    const personaRelevancyScoreMap: { [hubPersonaId: string]: number } = {};
    const queryTerms: string[] = normalizeQueryTerms(queryString);
    const normalizedPhoneNumber = normalizePhoneNumber(queryString);
    // Return personas which matches all query terms, or phone number.
    // Accepting just phone number, as phone numbers are often unique and may be specified with spaces, example: (425) 154 1563.
    const hubPersonaIds: string[] = folderHubPersonaIds.filter(
        hubPersonaId =>
            queryTerms.every(queryTerm =>
                matchPersona(queryTerm, hubPersonasMap[hubPersonaId], personaRelevancyScoreMap)
            ) ||
            matchPhone(
                normalizedPhoneNumber,
                hubPersonasMap[hubPersonaId],
                personaRelevancyScoreMap
            )
    );

    // Add current relevancyScore to persona, filter by queryToken constraints, and return
    return hubPersonaIds.map(hubPersonaId =>
        personaRelevancyScoreMap[hubPersonaId]
            ? {
                  ...hubPersonasMap[hubPersonaId],
                  relevancyScore: personaRelevancyScoreMap[hubPersonaId],
              }
            : hubPersonasMap[hubPersonaId]
    );
}

type QueryStringAndRootFolderId = {
    queryDataType: 'BasicQueryData';
    queryString: string;
    rootContactFolderId: string;
};

export function searchPeopleLocally(
    queryData: QueryData | QueryStringAndRootFolderId,
    hubPersonaList: HubPersona[]
): HubPersona[] {
    if (!hubPersonaList || hubPersonaList.length === 0) {
        return [];
    }
    const hubPersonasMap = hubPersonaList.reduce((map, hubPersona) => {
        map[hubPersona.hubPersonaId] = hubPersona;
        return map;
    }, {});

    const folderHubPersonaIds = hubPersonaList.map(hubPersona => hubPersona.hubPersonaId);
    const queryString =
        'queryDataType' in queryData && queryData.queryDataType === 'BasicQueryData'
            ? queryData.queryString
            : (queryData as QueryData).currentQueryString;

    return searchPeopleLocallyWithFolderMap(queryString, hubPersonasMap, folderHubPersonaIds);
}

function normalizeQueryTerms(query: string): string[] {
    return query
        .split(' ')
        .map(term => term.trim())
        .filter(term => term.length > 0);
}

export function normalizePhoneNumber(number: string): string {
    return number && number.replace(/\D/g, '');
}

const lowercaseRegExpMap: { [term: string]: RegExp } = {};
export function lowerCaseContains(haystack: string, needle: string): boolean {
    // Do not use Levenshtein unless query is 5 or more characters.
    const regExp = lowercaseRegExpMap.hasOwnProperty(needle)
        ? lowercaseRegExpMap[needle]
        : (lowercaseRegExpMap[needle] = toEscapedRegex(needle, /* caseInsensitive */ true));
    if (regExp.test(haystack)) {
        return true;
    } else if (needle.length < MIN_LEVENSHTEIN_WORD_LENGTH) {
        return false;
    }

    if (haystack && needle) {
        return levenshtein.get(haystack, needle, false) <= MAX_LEVENSHTEIN_DISTANCE;
    }

    return false;
}

export function matchPhone(
    phoneQuery: string,
    persona: HubPersona,
    personaRelevancyScoreMap: { [personaId: string]: number }
): boolean {
    // Only check Person for phone number
    if (
        phoneQuery &&
        phoneQuery.trim().length > 0 &&
        persona.type === PersonType.Person &&
        persona.info.phoneNumber &&
        normalizePhoneNumber(persona.info.phoneNumber).indexOf(phoneQuery) > -1
    ) {
        personaRelevancyScoreMap[persona.hubPersonaId] = RELEVANCY_SCORE_PHONE;
        return true;
    }

    return false;
}

function matchLinkedContacts(
    queryTerm: string,
    person: Person,
    personaRelevancyScoreMap: { [hubPersonaId: string]: number }
): boolean {
    if (
        person.linkedContacts &&
        person.linkedContacts.some(contact => {
            if (contact.displayName && lowerCaseContains(contact.displayName, queryTerm)) {
                personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_DISPLAY_NAME;
                return true;
            } else if (
                contact.sourceDisplayName &&
                lowerCaseContains(contact.sourceDisplayName, queryTerm)
            ) {
                personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_DISPLAY_NAME;
                return true;
            } else if (contact.email && lowerCaseContains(contact.email, queryTerm)) {
                personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_EMAIL;
                return true;
            }
            return false;
        })
    ) {
        return true;
    }

    return false;
}

function matchPerson(
    queryTerm: string,
    person: Person,
    personaRelevancyScoreMap: { [hubPersonaId: string]: number }
): boolean {
    if (matchLinkedContacts(queryTerm, person, personaRelevancyScoreMap)) {
        return true;
    }

    // Check if persona properties contain query term
    if (
        (person.info.givenName && lowerCaseContains(person.info.givenName, queryTerm)) ||
        (person.info.originalDisplayName &&
            lowerCaseContains(person.info.originalDisplayName, queryTerm)) ||
        (person.info.surname && lowerCaseContains(person.info.surname, queryTerm))
    ) {
        personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_DISPLAY_NAME;
        return true;
    } else if (
        (person.info.allEmailAddresses &&
            person.info.allEmailAddresses.some(
                emailAddress => emailAddress && lowerCaseContains(emailAddress, queryTerm)
            )) ||
        (person.info.mainEmailAddress && lowerCaseContains(person.info.mainEmailAddress, queryTerm))
    ) {
        personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_EMAIL;
        return true;
    } else if (person.info.company && lowerCaseContains(person.info.company, queryTerm)) {
        personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_COMPANY;
        return true;
    } else if (
        (person.info.location && lowerCaseContains(person.info.location, queryTerm)) ||
        (person.info.homeCity && lowerCaseContains(person.info.homeCity, queryTerm)) ||
        (person.info.workCity && lowerCaseContains(person.info.workCity, queryTerm))
    ) {
        personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_LOCATION;
        return true;
    } else if (person.info.title && lowerCaseContains(person.info.title, queryTerm)) {
        personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_TITLE;
        return true;
    }

    return false;
}

function matchDisplayName(queryTerm: string, persona: HubPersona): boolean {
    const displayNameFirstLast = getHubPersonaDisplayNameFirstLast(persona);
    const displayNameLastFirst = getHubPersonaDisplayNameLastFirst(persona);
    return (
        (displayNameFirstLast && lowerCaseContains(displayNameFirstLast, queryTerm)) ||
        (displayNameLastFirst && lowerCaseContains(displayNameLastFirst, queryTerm))
    );
}

// Checks if query term matches a private distribution list.
// Intentionally not checking every member for a match.
function matchPDL(
    queryTerm: string,
    privateDistributionList: PrivateDistributionList,
    personaRelevancyScoreMap: { [hubPersonaId: string]: number }
): boolean {
    if (
        privateDistributionList.info.listOriginalDisplayName &&
        lowerCaseContains(privateDistributionList.info.listOriginalDisplayName, queryTerm)
    ) {
        personaRelevancyScoreMap[
            privateDistributionList.hubPersonaId
        ] = RELEVANCY_SCORE_DISPLAY_NAME;
        return true;
    } else if (
        privateDistributionList.notes &&
        lowerCaseContains(privateDistributionList.notes, queryTerm)
    ) {
        personaRelevancyScoreMap[privateDistributionList.hubPersonaId] = RELEVANCY_SCORE_NOTES;
        return true;
    }

    return false;
}

function matchFavoritePerson(
    queryTerm: string,
    person: FavoriteEntity,
    personaRelevancyScoreMap: { [hubPersonaId: string]: number }
): boolean {
    // Check if persona properties contain query term
    if (person.info.displayName && lowerCaseContains(person.info.displayName, queryTerm)) {
        personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_DISPLAY_NAME;
        return true;
    } else if (
        (person.type === PersonType.FavoritePerson &&
            person.info.allEmailAddresses &&
            person.info.allEmailAddresses.some(
                emailAddress => emailAddress && lowerCaseContains(emailAddress, queryTerm)
            )) ||
        (person.type === PersonType.FavoritePerson &&
            person.info.mainEmailAddress &&
            lowerCaseContains(person.info.mainEmailAddress, queryTerm))
    ) {
        personaRelevancyScoreMap[person.hubPersonaId] = RELEVANCY_SCORE_EMAIL;
        return true;
    }

    return false;
}

function matchPersona(
    queryTerm: string,
    persona: HubPersona,
    personaRelevancyScoreMap: { [hubPersonaId: string]: number }
): boolean {
    if (!persona) {
        return false;
    }

    if (matchDisplayName(queryTerm, persona)) {
        personaRelevancyScoreMap[persona.hubPersonaId] = RELEVANCY_SCORE_DISPLAY_NAME;
        return true;
    }

    if (persona.type === PersonType.Person) {
        // Check if linked contacts contain query term
        return matchPerson(queryTerm, persona, personaRelevancyScoreMap);
    } else if (
        persona.type === PersonType.FavoritePerson ||
        persona.type === PersonType.FavoritePrivateDistributionList ||
        persona.type === PersonType.FavoriteUnifiedGroup
    ) {
        return matchFavoritePerson(queryTerm, persona, personaRelevancyScoreMap);
    } else if (persona.type === PersonType.PrivateDistributionList) {
        return matchPDL(queryTerm, persona, personaRelevancyScoreMap);
    }

    return false;
}
